import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  updateDoc,
  addDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  getCurrentUser, 
  isAdmin, 
  logActivity
} from './authorizationService';
import { AUDIT_ACTIONS, RESOURCE_TYPES, USER_ROLES } from '../types/rbac';

/**
 * Admin Service
 * Handles admin-only operations with proper security
 */

/**
 * Make a user an admin (admin only)
 * @param {string} targetUserEmail - Email of user to make admin
 * @returns {Promise<boolean>} Success status
 */
export const makeUserAdmin = async (targetUserEmail) => {
  try {
    // Security check: Only admins can make other users admin
    if (!isAdmin()) {
      throw new Error('Unauthorized: Only admins can make users admin');
    }

    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    console.log(`Admin ${currentUser.email} attempting to make ${targetUserEmail} an admin`);

    // Find target user by email
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', targetUserEmail));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error(`User not found: ${targetUserEmail}`);
    }

    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();

    // Check if user is already admin
    if (userData.role === USER_ROLES.ADMIN) {
      console.log(`User ${targetUserEmail} is already an admin`);
      return true;
    }

    // Update user to admin
    const updateData = {
      role: USER_ROLES.ADMIN,
      accountId: null, // Admins don't belong to a specific account
      accountName: null,
      updatedAt: serverTimestamp()
    };

    await updateDoc(doc(db, 'users', userDoc.id), updateData);

    // Log the admin action
    await logActivity(AUDIT_ACTIONS.USER_ROLE_CHANGED, RESOURCE_TYPES.USER, userDoc.id, {
      targetUserEmail,
      newRole: USER_ROLES.ADMIN,
      previousRole: userData.role || USER_ROLES.CLIENT
    });

    console.log(`✅ Successfully made ${targetUserEmail} an admin`);
    return true;

  } catch (error) {
    console.error('Error making user admin:', error);
    throw error;
  }
};

/**
 * Remove admin privileges from a user (admin only)
 * @param {string} targetUserEmail - Email of user to remove admin from
 * @param {string} newAccountId - Account ID to assign user to
 * @returns {Promise<boolean>} Success status
 */
export const removeUserAdmin = async (targetUserEmail, newAccountId) => {
  try {
    // Security check: Only admins can remove admin privileges
    if (!isAdmin()) {
      throw new Error('Unauthorized: Only admins can remove admin privileges');
    }

    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // Prevent self-demotion
    if (currentUser.email === targetUserEmail) {
      throw new Error('Cannot remove your own admin privileges');
    }

    console.log(`Admin ${currentUser.email} attempting to remove admin from ${targetUserEmail}`);

    // Find target user by email
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', targetUserEmail));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error(`User not found: ${targetUserEmail}`);
    }

    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();

    if (userData.role !== USER_ROLES.ADMIN) {
      console.log(`User ${targetUserEmail} is not an admin`);
      return true;
    }

    // Get account name
    let accountName = 'Unknown Account';
    if (newAccountId) {
      try {
        const accountDoc = await getDoc(doc(db, 'accounts', newAccountId));
        if (accountDoc.exists()) {
          accountName = accountDoc.data().name;
        }
      } catch (error) {
        console.warn('Could not fetch account name:', error);
      }
    }

    // Update user to client
    const updateData = {
      role: USER_ROLES.CLIENT,
      accountId: newAccountId,
      accountName: accountName,
      updatedAt: serverTimestamp()
    };

    await updateDoc(doc(db, 'users', userDoc.id), updateData);

    // Log the admin action
    await logActivity(AUDIT_ACTIONS.USER_ROLE_CHANGED, RESOURCE_TYPES.USER, userDoc.id, {
      targetUserEmail,
      newRole: USER_ROLES.CLIENT,
      previousRole: USER_ROLES.ADMIN,
      newAccountId,
      newAccountName: accountName
    });

    console.log(`✅ Successfully removed admin privileges from ${targetUserEmail}`);
    return true;

  } catch (error) {
    console.error('Error removing admin privileges:', error);
    throw error;
  }
};

/**
 * Get all users (admin only)
 * @returns {Promise<Array>} List of users
 */
export const getAllUsers = async () => {
  try {
    // Security check: Only admins can view all users
    if (!isAdmin()) {
      throw new Error('Unauthorized: Only admins can view all users');
    }

    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

/**
 * Get user by email (admin only)
 * @param {string} email - User email
 * @returns {Promise<Object|null>} User data or null
 */
export const getUserByEmail = async (email) => {
  try {
    // Security check: Only admins can view user details
    if (!isAdmin()) {
      throw new Error('Unauthorized: Only admins can view user details');
    }

    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const userDoc = querySnapshot.docs[0];
    return {
      id: userDoc.id,
      ...userDoc.data()
    };

  } catch (error) {
    console.error('Error getting user by email:', error);
    throw error;
  }
};

/**
 * Create a new account (admin only)
 * @param {string} accountName - Name of the account
 * @param {string} description - Account description
 * @returns {Promise<string>} Account ID
 */
export const createAccount = async (accountName, description = '') => {
  try {
    // Security check: Only admins can create accounts
    if (!isAdmin()) {
      throw new Error('Unauthorized: Only admins can create accounts');
    }

    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    const accountData = {
      name: accountName,
      description,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: currentUser.uid
    };

    const docRef = await addDoc(collection(db, 'accounts'), accountData);

    // Log the admin action
    await logActivity(AUDIT_ACTIONS.ACCOUNT_CREATED, RESOURCE_TYPES.ACCOUNT, docRef.id, {
      accountName,
      description
    });

    console.log(`✅ Successfully created account: ${accountName}`);
    return docRef.id;

  } catch (error) {
    console.error('Error creating account:', error);
    throw error;
  }
};

/**
 * Assign user to account (admin only)
 * @param {string} userEmail - User email
 * @param {string} accountId - Account ID
 * @returns {Promise<boolean>} Success status
 */
export const assignUserToAccount = async (userEmail, accountId) => {
  try {
    // Security check: Only admins can assign users to accounts
    if (!isAdmin()) {
      throw new Error('Unauthorized: Only admins can assign users to accounts');
    }

    // Find user by email
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', userEmail));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error(`User not found: ${userEmail}`);
    }

    const userDoc = querySnapshot.docs[0];

    // Get account name
    const accountDoc = await getDoc(doc(db, 'accounts', accountId));
    if (!accountDoc.exists()) {
      throw new Error(`Account not found: ${accountId}`);
    }

    const accountData = accountDoc.data();

    // Update user with account assignment
    const updateData = {
      accountId: accountId,
      accountName: accountData.name,
      updatedAt: serverTimestamp()
    };

    await updateDoc(doc(db, 'users', userDoc.id), updateData);

    // Log the admin action
    await logActivity(AUDIT_ACTIONS.USER_ASSIGNED_TO_ACCOUNT, RESOURCE_TYPES.USER, userDoc.id, {
      userEmail,
      accountId,
      accountName: accountData.name
    });

    console.log(`✅ Successfully assigned ${userEmail} to account: ${accountData.name}`);
    return true;

  } catch (error) {
    console.error('Error assigning user to account:', error);
    throw error;
  }
};
